import { luggageShape, quotationFlightShape } from "app/utils/propTypes";
import PropTypes from "prop-types";
import { memo, useContext, useMemo } from "react";
import "./SelectedFlightRecap.scss";
import ItineraryViewRecap from "app/pages/Booking/Quotation/Flight/SelectedFlightRecap/ItineraryViewRecap";
import { buildStopOverItineraryPreviewData } from "app/pages/.shared/ItineraryUtils/ItineraryUtils";
import LuggageSummaryRecap from "app/pages/Booking/Quotation/Flight/SelectedFlightRecap/LuggageSummaryRecap";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import AppGlobalsContext from "app/AppGlobalsContext";
import { FormattedMessage } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import { Button } from "app/components/ui/Button/Button";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { ArrowRightIcon } from "app/icons/navigation";
import AirplaneFilledIcon from "app/icons/travel/AirplaneFilledIcon";
import { ICON_SIZES } from "app/constants";

const locationWithoutIATA = location => location?.replace(/\s?\(.*?\)/g, "");

const SelectedFlightRecap = ({
	flight = {},
	luggageMaxCount,
	isLoading,
	flightLuggageOption = {},
	departureCity,
	destination,
	onClick = () => {},
}) => {
	const { resolution } = useContext(AppGlobalsContext);

	const { track } = useAnalytics();

	const isMobile = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;
	const inboundFlightData = useMemo(
		() => buildStopOverItineraryPreviewData(flight?.inbound),
		[flight?.inbound]
	);

	const outboundFlightData = useMemo(
		() => buildStopOverItineraryPreviewData(flight?.outbound),
		[flight?.outbound]
	);

	const buttonLabel = (
		<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} isBold component="span">
			<FormattedMessage id="stop.over.flight.duration.more.details" />
		</Typography>
	);

	const handleOpenFromLink = event => {
		event.stopPropagation(); // avoid triggering the block's onClick onmobile
		track("stopover_selected_flight_recap_link_clicked");
		onClick();
	};

	const handleOpenFromBlock = () => {
		track("stopover_selected_flight_recap_block_clicked");
		onClick();
	};

	return (
		<div className="selected-flight-recap">
			<div className="selected-flight-recap__header">
				{isLoading ? (
					<>
						{isMobile ? (
							<LoaderBar height={16} width={270} />
						) : (
							<>
								<LoaderBar height={16} width={270} />
								<LoaderBar height={16} width={170} />
							</>
						)}
					</>
				) : (
					<>
						<div className="selected-flight-recap__title" onClick={handleOpenFromBlock}>
							<AirplaneFilledIcon size={ICON_SIZES.XS} />
							<Typography
								variant={TYPOGRAPHY_VARIANTS.LARGE}
								variantMD={TYPOGRAPHY_VARIANTS.XL}
								isBold
								component="span"
							>
								<FormattedMessage
									id="stop.over.selected.flight.title"
									values={{
										departureCity: locationWithoutIATA(departureCity),
										destination: locationWithoutIATA(destination),
									}}
								/>
							</Typography>
						</div>

						{!isMobile && (
							<Button
								variant="borderless"
								size="sm"
								endIcon={<ArrowRightIcon />}
								className="selected-flight-recap__edit"
								onClick={handleOpenFromLink}
							>
								{buttonLabel}
							</Button>
						)}
					</>
				)}
			</div>
			<div className="selected-flight-recap__block" onClick={handleOpenFromBlock}>
				<div className="selected-flight-recap__flight">
					<div className="selected-flight-recap__flight-outbound">
						<ItineraryViewRecap
							label="quotation.flight.outbound.title"
							flightData={outboundFlightData}
						/>
					</div>
					<div className="selected-flight-recap__flight-inbound">
						<ItineraryViewRecap
							label="quotation.flight.inbound.title"
							flightData={inboundFlightData}
						/>
					</div>
				</div>

				<div className="selected-flight-recap__luggage">
					<LuggageSummaryRecap
						luggageMaxCount={luggageMaxCount}
						isLoading={isLoading}
						flightLuggageOption={flightLuggageOption}
					/>
				</div>

				{isMobile && (
					<Button
						variant="borderless"
						size="sm"
						endIcon={<ArrowRightIcon />}
						className="selected-flight-recap__edit"
						onClick={handleOpenFromLink}
					>
						{isLoading ? <LoaderBar height={8} width={170} /> : buttonLabel}
					</Button>
				)}
			</div>
		</div>
	);
};

SelectedFlightRecap.propTypes = {
	flight: quotationFlightShape,
	luggageMaxCount: PropTypes.number,
	isLoading: PropTypes.bool,
	departureCity: PropTypes.string,
	destination: PropTypes.string,
	onClick: PropTypes.func,
	flightLuggageOption: luggageShape,
};

export default memo(SelectedFlightRecap);
